import { ColorResult, SketchPicker } from 'react-color';
import Modal from '../modal/modal';
import { useEffect, useState } from 'react';
import InlineLoadingSpinner from '../inline-loading-spinner/inline-loading-spinner';
import ConfirmationModal from '../confirmation-modal/confirmation-modal';
import { useTranslation } from 'react-i18next';
import { UPDATE_CURRENT_COMPANY } from '../api';
import { useMutation } from '@apollo/client';

export interface ColorPickerModalProps {
  title?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedColor: {
    id: string;
    color: ColorResult | string;
  };
  setSelectedColor: (color: {
    id: string;
    color: ColorResult | string;
  }) => void;
  nextLevelKeys: any[];
  presetColors: string[];
  refetchCompany: () => void;
}

export function ColorPickerModal({
  title,
  open,
  setOpen,
  selectedColor,
  setSelectedColor,
  nextLevelKeys,
  presetColors,
  refetchCompany,
}: ColorPickerModalProps) {
  const [color, setColor] = useState<{
    color: ColorResult | string;
    id: string;
  }>({ color: {} as ColorResult, id: '' });
  const { t } = useTranslation(['common']);

  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [confirmationAction, setConfirmationAction] = useState<
    null | 'save' | 'cancel'
  >(null);
  const [updateCompany] = useMutation(UPDATE_CURRENT_COMPANY);

  const saveColor = () => {
    const newNextLevelKeys = nextLevelKeys.map((item) => {
      if (item.id === selectedColor.id) {
        return {
          id: item.id,
          name: item.name,
          color:
            typeof color.color === 'string' ? color.color : color.color.hex,
        };
      }
      return {
        id: item.id,
        name: item.name,
        color: item.color,
      };
    });
    setSelectedColor({ color: '', id: '' });
    updateCompany({
      variables: {
        input: {
          orgTreeNextLevelKeys: newNextLevelKeys,
        },
      },
    })
      .then(() => {
        if (refetchCompany) {
          refetchCompany();
        }
        setOpen(false);
      })
      .catch((error) => {
        console.error('Error updating document: ', error);
      });
  };

  useEffect(() => {
    if (selectedColor.color && selectedColor.id) {
      setColor(selectedColor);
    }
  }, [selectedColor?.color, selectedColor?.id]);

  return (
    <Modal open={open} setOpen={setOpen} disableCloseOnOutsideClick>
      <h3 className="text-center mb-2">{t(title || '')}</h3>
      {color.color && color.id ? (
        <SketchPicker
          color={
            !color
              ? typeof selectedColor.color === 'string'
                ? selectedColor.color
                : selectedColor.color.hex
              : typeof color.color === 'string'
              ? color.color
              : color.color.hex
          }
          onChangeComplete={(color: any) => {
            setColor({ color, id: selectedColor.id });
          }}
          presetColors={presetColors}
        />
      ) : (
        <InlineLoadingSpinner />
      )}
      <div>
        <button
          className="w-full bg-light-primary text-white mt-3 rounded-md py-2 px-4 hover:bg-light-primary-dark transition-colors duration-300 ease-in-out shadow-md hover:shadow-lg"
          onClick={() => {
            setShowConfirmationModal(true);
            setConfirmationAction('save');
          }}
        >
          {t('Save')}
        </button>
        <button
          className="w-full bg-semantic-error text-white mt-3 rounded-md py-2 px-4 hover:bg-red-400 transition-colors duration-300 ease-in-out shadow-md hover:shadow-lg"
          onClick={() => {
            setShowConfirmationModal(true);
            setConfirmationAction('cancel');
          }}
        >
          {t('Cancel')}
        </button>
      </div>
      <ConfirmationModal
        text={
          confirmationAction === 'save'
            ? t('Are you sure you want to save the color?')
            : t('Are you sure you want to discard the changes?')
        }
        open={showConfirmationModal}
        setOpen={setShowConfirmationModal}
        onConfirm={() => {
          if (confirmationAction === 'save') {
            saveColor();
          } else {
            setSelectedColor({ color: '', id: '' });
          }
        }}
        onCancel={() => {
          setShowConfirmationModal(false);
        }}
        confirmButtonColorClasses={
          confirmationAction === 'save'
            ? 'bg-light-primary'
            : 'bg-semantic-error'
        }
      />
    </Modal>
  );
}

export default ColorPickerModal;
